import { ENDPOINTS } from '../utils/endpoints';
import { getAPIwithToken, postAPIwithToken } from '../utils/fetchApi';

export interface WalletFiltersPayload {
  search?: string;
  routeId?: number;
  depoId?: number;
  zoneId?: number;
  cityId?: number;
  status?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface WalletSubObject {
  id?: number;
  currentBalance?: number;
  creditLimit?: number;
  totalAvailableBalance?: number;
  lastTransactionAt?: string;
  status?: number;
  updatedAt?: string;
}

export interface WalletAddress {
  id?: number;
  addressType?: string;
  addressLine1?: string;
  addressLine2?: string | null;
  pincode?: string;
  isDefault?: boolean;
  cityId?: number;
  cityName?: string;
  routeId?: number;
  routeName?: string;
  routeSapCode?: string;
  depoId?: number;
  depoName?: string;
  depoSapCode?: string;
  zoneId?: number;
  zoneName?: string;
}

export interface DistributorWalletItem {
  id: number;
  distributorId?: number;
  loginId?: string;
  customerId?: string;
  sapCode?: string;
  sapCustomerId?: string;
  companyName: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  email?: string;
  phone?: string;
  alternatePhone?: string | null;
  status?: string;
  paymentTerms?: string;
  gst?: string;
  panNo?: string;
  fassiNo?: string;

  currentBalance?: number;
  walletBalance?: number;
  creditLimit?: number;
  totalAvailableBalance?: number;
  wallet?: WalletSubObject;

  routeId?: number;
  routeName?: string;
  routeSapCode?: string;
  route?: { id?: number; name?: string; sapCode?: string; depoId?: number };

  depoId?: number;
  depoName?: string;
  depoSapCode?: string;
  depoSapName?: string;
  depo?: { id?: number; name?: string; sapCode?: string; sapName?: string; zoneId?: number };

  cityId?: number;
  cityName?: string;
  city?: { id?: number; name?: string };

  zoneId?: number;
  zoneName?: string;
  zone?: { id?: number; name?: string };

  addresses?: WalletAddress[];
  createdAt?: string;
  updatedAt?: string;
}

export interface WalletPagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface WalletSummary {
  totalDistributors: number;
  pageDistributors?: number;
  totalWalletBalance: number;
  totalCreditLimit: number;
}

export interface WalletListResponse {
  status: string;
  message?: string;
  data: DistributorWalletItem[];
  pagination?: WalletPagination;
  summary?: WalletSummary;
}

export interface WalletTransactionItem {
  id: number | string;
  transactionType?: string;
  amount?: number;
  previousBalance?: number;
  newBalance?: number;
  description?: string;
  referenceNo?: string;
  createdAt?: string;
  status?: string;
}

export interface SingleWalletDetailResponse {
  status: string;
  message?: string;
  data: DistributorWalletItem & {
    transactions?: WalletTransactionItem[];
  };
}

export interface UploadRowDetail {
  sapCode?: string;
  sapCustomerId: string;
  status: string;
  oldBalance?: number;
  newBalance?: number;
  oldCreditLimit?: number;
  newCreditLimit?: number;
  adjustmentAmount?: number;
  message?: string;
}

export interface UploadWalletResponse {
  status: string;
  message: string;
  data?: {
    totalRows: number;
    successCount: number;
    warningCount: number;
    failedCount: number;
    details?: UploadRowDetail[];
  };
}

export const getDistributorWalletsAPI = async (filters: WalletFiltersPayload = {}): Promise<WalletListResponse> => {
  const queryParams = new URLSearchParams();
  if (filters.search) queryParams.append('search', filters.search);
  if (filters.routeId !== undefined && filters.routeId !== null) queryParams.append('routeId', String(filters.routeId));
  if (filters.depoId !== undefined && filters.depoId !== null) queryParams.append('depoId', String(filters.depoId));
  if (filters.zoneId !== undefined && filters.zoneId !== null) queryParams.append('zoneId', String(filters.zoneId));
  if (filters.cityId !== undefined && filters.cityId !== null) queryParams.append('cityId', String(filters.cityId));
  if (filters.status) queryParams.append('status', filters.status);
  if (filters.page) queryParams.append('page', String(filters.page));
  if (filters.limit) queryParams.append('limit', String(filters.limit));
  if (filters.sortBy) queryParams.append('sortBy', filters.sortBy);
  if (filters.sortOrder) queryParams.append('sortOrder', filters.sortOrder);

  const queryString = queryParams.toString();
  const endpoint = queryString ? `${ENDPOINTS.DISTRIBUTOR_WALLET}?${queryString}` : ENDPOINTS.DISTRIBUTOR_WALLET;
  return await getAPIwithToken<WalletListResponse>(endpoint);
};

export const getDistributorWalletByIdAPI = async (id: number | string): Promise<SingleWalletDetailResponse> => {
  return await getAPIwithToken<SingleWalletDetailResponse>(ENDPOINTS.DISTRIBUTOR_WALLET_BY_ID(id));
};

export const uploadWalletBalancesAPI = async (file: File): Promise<UploadWalletResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  return await postAPIwithToken<UploadWalletResponse>(ENDPOINTS.DISTRIBUTOR_WALLET_UPLOAD, formData);
};

export const downloadWalletTemplateAPI = async (): Promise<void> => {
  const baseUrl: string = import.meta.env.VITE_GOWARDHAN_ERP_BASE_URL || '';
  const cleanBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const token: string | null = localStorage.getItem('gowardhan_erp_token');

  const response = await fetch(`${cleanBase}/${ENDPOINTS.DISTRIBUTOR_WALLET_TEMPLATE}`, {
    method: 'GET',
    headers: {
      'Authorization': token ? `Bearer ${token}` : '',
      'ngrok-skip-browser-warning': 'true',
    },
  });

  if (!response.ok) {
    let errorMsg = `Download failed with status ${response.status}`;
    try {
      const errJson = await response.json();
      if (errJson.message) errorMsg = errJson.message;
    } catch {
      // Ignore parse error
    }
    throw new Error(errorMsg);
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'wallet_balance_template.xlsx';
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};
